// @ts-nocheck
/**
 * AllAppsLayout: reusable 300px panel that attaches flush to the 96px Sidebar rail.
 *
 * Reads ALL_APPS from Sidebar/constants as the single source of truth, so adding
 * a new app or sub-item there automatically shows up here (no hardcoding).
 * Icons come from FlyServiceIcon in Sidebar/Flyouts, identical to the AllAppsFlyout popup.
 * Each sub-item is a React Router NavLink, so routes stay clickable with active state.
 *
 * Pass as the `panel` prop to AppLayout:
 *   <AppLayout panel={<AllAppsLayout />}><Outlet /></AppLayout>
 */
import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { ALL_APPS, DEFAULT, isAppVisibleForRole } from '../Sidebar/constants';
import { FlyServiceIcon } from '../Sidebar/Flyouts';
import { useRole } from '../../context/RoleContext';

const ACCENT = '#15A08C';
const LINE = '#E8EDF3';

export default function AllAppsLayout(): React.ReactElement {
  const location = useLocation();
  const { role: userRole } = useRole();

  // Auto-expand whichever app owns the current pathname
  const activeApp = ALL_APPS.find(function (app) {
    return app.items && app.items.some(function (item) {
      return location.pathname === item.path ||
             (item.path !== '/' && location.pathname.startsWith(item.path));
    });
  });

  const [expandedId, setExpandedId] = useState(activeApp ? activeApp.id : 'accounting');

  return (
    <div style={{
      width: 300,
      flexShrink: 0,
      background: '#F7F8FA',
      borderRight: '1px solid ' + LINE,
      padding: '8px 12px',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{
        padding: '6px 6px 10px',
        fontSize: 14,
        fontWeight: 700,
        color: '#0F172A',
      }}>
        All Apps
        <span style={{ color: '#0F6E56', fontSize: 10, marginLeft: 8, fontWeight: 600 }}>
          ● pinned
        </span>
      </div>

      {ALL_APPS.filter(function (app) { return isAppVisibleForRole(app.id, userRole); }).map(function (app) {
        const isExpanded = expandedId === app.id;
        return (
          <div key={app.id} style={{ marginBottom: 2 }}>
            <div
              onClick={function () { setExpandedId(isExpanded ? null : app.id); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px',
                borderRadius: 8,
                cursor: 'pointer',
                background: isExpanded ? app.color + '1A' : 'transparent',
                transition: 'background 0.15s',
              }}
              onMouseEnter={function (e) { if (!isExpanded) e.currentTarget.style.background = '#F1F5F9'; }}
              onMouseLeave={function (e) { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                background: '#1E293B',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <FlyServiceIcon id={app.id} color={app.color} />
              </div>
              <span style={{
                flex: 1,
                fontSize: 13,
                fontWeight: isExpanded ? 600 : 500,
                color: isExpanded ? app.color : DEFAULT,
              }}>
                {app.label}
              </span>
              {app.premium && (
                <span style={{
                  fontSize: 9,
                  color: '#F97316',
                  background: 'rgba(249,115,22,0.1)',
                  padding: '1px 5px',
                  borderRadius: 4,
                  marginRight: 4,
                  fontWeight: 700,
                }}>PRO</span>
              )}
              {isExpanded
                ? <ChevronDown size={13} color={app.color} />
                : <ChevronRight size={13} color="#CBD5E1" />}
            </div>

            {isExpanded && app.items && (
              <div style={{ paddingLeft: 12, marginBottom: 4 }}>
                {app.items.map(function (item) {
                  const isActive = location.pathname === item.path ||
                                   (item.path !== '/' && location.pathname.startsWith(item.path));
                  return (
                    <NavLink
                      key={item.label}
                      to={item.path}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 12px',
                        borderRadius: 6,
                        textDecoration: 'none',
                        fontSize: 13,
                        fontWeight: isActive ? 700 : 500,
                        color: isActive ? ACCENT : DEFAULT,
                        background: isActive ? 'rgba(21,160,140,0.06)' : 'transparent',
                        transition: 'all 0.12s',
                        marginBottom: 1,
                      }}
                      onMouseEnter={function (e) {
                        if (!isActive) {
                          e.currentTarget.style.background = '#F1F5F9';
                          e.currentTarget.style.color = ACCENT;
                        }
                      }}
                      onMouseLeave={function (e) {
                        if (!isActive) {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = DEFAULT;
                        }
                      }}
                    >
                      {isActive && (
                        <div style={{
                          width: 4,
                          height: 4,
                          borderRadius: '50%',
                          background: ACCENT,
                          flexShrink: 0,
                        }} />
                      )}
                      {item.label}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
